import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { ProtectedRoute } from '@/components/protected-route';
import { MainLayout } from '@/components/main-layout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useJournals } from '@/hooks/use-journals';
import { JournalTable } from '@/components/journals/journal-table';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { JournalDetailDialog } from '@/components/journals/journal-detail-dialog';
import { JournalFilters } from '@/components/journals/journal-filters';
import { PageHeader } from '@/components/page-header';
import { JournalEntry } from '@/types';
import { format } from 'date-fns';
import { clampPage, resolveTotalItems, resolveTotalPages } from '@/lib/pagination';

export const Route = createFileRoute('/journals/')({
  component: () => (
    <ProtectedRoute>
      <MainLayout>
        <JournalsPage />
      </MainLayout>
    </ProtectedRoute>
  ),
});

function JournalsPage() {
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    startDate: undefined as Date | undefined,
    endDate: undefined as Date | undefined,
  });
  const [page, setPage] = useState(1);
  const [selectedJournal, setSelectedJournal] = useState<JournalEntry | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const handleFiltersChange = (nextFilters: typeof filters) => {
    setPage(1);
    setFilters(nextFilters);
  };

  const { data: journalsResponse, isLoading, error } = useJournals({
    search: filters.search || undefined,
    type: filters.type !== 'all' ? filters.type : undefined,
    page,
    startDate: filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : undefined,
    endDate: filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : undefined,
  });
  
  const journals = journalsResponse?.data || [];
  const pagination = journalsResponse?.pagination;
  const totalItems = resolveTotalItems(pagination, journals.length);
  const totalPages = resolveTotalPages(pagination, journals.length);

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader title="Journal Entries" />

      {/* Advanced Filters */}
      <JournalFilters filters={filters} onFiltersChange={handleFiltersChange} />

      {/* Journals Table */}
      <Card>
        <CardHeader>
          <CardTitle>General Journal</CardTitle>
          <CardDescription>
            View all double-entry accounting journal entries.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <JournalTable 
            journals={journals} 
            isLoading={isLoading} 
            error={error} 
            onViewDetail={(journal) => {
              setSelectedJournal(journal);
              setIsDetailOpen(true);
            }}
          />
          
          {pagination && (
             <div className="mt-4">
                <PaginationControls 
                   currentPage={page}
                   totalPages={totalPages}
                   onPageChange={(nextPage) => setPage(clampPage(nextPage, totalPages))}
                   totalItems={totalItems}
                />
             </div>
          )}
        </CardContent>
      </Card>
      
      <JournalDetailDialog 
        open={isDetailOpen} 
        onOpenChange={setIsDetailOpen} 
        journal={selectedJournal} 
      />

    </div>
  );
}
